/**
 * @fileoverview The household subscription, on the parent's own page (F2).
 *
 * The unit of sale is the household, so the card says so in the first line —
 * "all your children, every teacher, one price" is the whole proposition and
 * a parent with two children should not have to work it out.
 *
 * Three states, and they are genuinely different:
 *
 * - **Covered by a gift** (I12): says who it came from and until when, and
 *   offers no checkout. Selling somebody something they already have is the
 *   fastest way to look like you weren't paying attention.
 * - **Covered by a subscription**: manage-in-Stripe only. There is no cancel
 *   button here on purpose; a second one would be a second thing that can
 *   disagree with the first.
 * - **Not covered**: the two plans, with the annual quoted per month, since
 *   "under $5 a month, billed yearly" is the sentence the price was chosen
 *   for.
 *
 * The whole card hides itself when the API says nothing is purchasable and
 * nothing is covered — no point advertising a plan that cannot be bought.
 */

import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import parentApi, { getParentErrorMessage } from "@/lib/parentApi";
import { Card, SectionHeader, Button } from "@/components/studio/primitives";

export function money(cents) {
  if (cents == null) return "";
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

function until(value) {
  const when = new Date(value);
  return Number.isNaN(when.getTime())
    ? ""
    : when.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

export function FamilyPlanCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await parentApi.get("/parent/subscription");
      setState(data);
    } catch {
      setState(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!state) return null;
  // Nothing to sell and nothing held — advertising a plan that cannot be
  // bought is worse than saying nothing.
  if (!state.covered && !state.purchasable) return null;

  const buy = async (plan) => {
    setBusy(true);
    try {
      const { data } = await parentApi.post("/parent/subscription/checkout", { plan });
      window.location.href = data.url;
    } catch (err) {
      toast.error(getParentErrorMessage(err, "Couldn't start checkout"));
      setBusy(false);
    }
  };

  const manage = async () => {
    try {
      const { data } = await parentApi.post("/parent/subscription/portal");
      window.location.href = data.url;
    } catch (err) {
      toast.error(getParentErrorMessage(err, "Couldn't open billing"));
    }
  };

  if (state.covered) {
    const gift = state.source === "gift";
    return (
      <Card className="mb-[22px]" data-testid="family-plan">
        <SectionHeader>Your family plan</SectionHeader>
        <p className="font-sans text-[14px] text-[var(--text-secondary)]" data-testid="family-covered">
          {gift
            ? `A gift from your teacher, through ${until(state.covered_until)}.`
            : `Active${state.covered_until ? ` through ${until(state.covered_until)}` : ""}.`}
        </p>
        {state.allowance ? (
          <p className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="family-allowance">
            {state.allowance.minutes_remaining} of {state.allowance.minutes_limit} practice-feedback minutes left
            this month.
          </p>
        ) : null}
        {!gift ? (
          <div className="mt-4">
            <Button variant="ghost" onPress={manage} data-testid="family-manage">
              Manage billing
            </Button>
          </div>
        ) : null}
      </Card>
    );
  }

  const monthly = state.prices?.monthly;
  const annual = state.prices?.annual;

  return (
    <Card className="mb-[22px]" data-testid="family-plan">
      <SectionHeader>Hans for families</SectionHeader>
      <p className="font-sans text-[14px] text-[var(--text-secondary)]">
        All your children, every teacher they study with, one price.
      </p>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <div className="flex-1 rounded-2xl border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-4">
          <p className="font-mono text-2xl text-[var(--text-primary)]">{money(monthly?.cents)}</p>
          <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">a month</p>
          <Button className="mt-3" onPress={() => buy("monthly")} disabled={busy} data-testid="family-buy-monthly">
            Choose monthly
          </Button>
        </div>
        <div className="flex-1 rounded-2xl border border-[var(--accent-yellow)] bg-[var(--accent-yellow-faint)] p-4">
          <p className="font-mono text-2xl text-[var(--text-primary)]">{money(annual?.cents)}</p>
          <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
            a year — {money(annual?.monthly_equivalent_cents)} a month
          </p>
          <Button className="mt-3" onPress={() => buy("annual")} disabled={busy} data-testid="family-buy-annual">
            Choose yearly
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default FamilyPlanCard;
